import { APIClient } from "api/apiCore";
import { setActiveMemberrole, setActivePostId2 } from "../redux/workplace/wpActions";
import {
    getChatUserDetails2s,
    getChatUserConversations2,
    readConversation,
    changeSelectedChat,
    setActiveGroupId,
    changeTab,
} from "../redux/actions";
import { TABS, WpModes } from "constants/layout";
import { MemberRole } from "../redux/workplace/wpTypes";
import { Utils } from "./utils";
import { getStore } from "../redux/store";

export interface NotificationTarget {
    gtc?: string | null; // go-to-chat
    wpc?: string | null; // go-to-workplace
    tkc?: string | null; // go-to-task
    apv?: string | null; // go-to-approval
}

// Mirrors PUSH_CLICK_* in public/firebase-messaging-sw.js and buildRedirectUrl in
// ChatMessagingService.java. Used to interpret the raw clickname/clickvalue that arrive
// via @capacitor-firebase/messaging's 'notificationActionPerformed' event (iOS/Android tap
// while the app is alive), where there's no "/redirect?..." URL string to parse.
const PUSH_CLICK_OPENCHAT = "1";
const PUSH_CLICK_WP = "5";
const PUSH_CLICK_WPNOTICE = "6";
const PUSH_CLICK_TASK = "8";
const PUSH_CLICK_TASKNOTIFY = "9";
const PUSH_CLICK_APPROVAL = "10";

export function mapClickToTarget(clickname?: string | null, clickvalue?: string | null): NotificationTarget | null {
    if (!clickname || !clickvalue) return null;
    if (clickname === PUSH_CLICK_OPENCHAT) return { gtc: clickvalue };
    if (clickname === PUSH_CLICK_WP || clickname === PUSH_CLICK_WPNOTICE) return { wpc: clickvalue };
    if (clickname === PUSH_CLICK_TASK || clickname === PUSH_CLICK_TASKNOTIFY) return { tkc: clickvalue };
    if (clickname === PUSH_CLICK_APPROVAL) return { apv: clickvalue };
    return null;
}

// Parses the "/redirect?gtc=..&wpc=..&tkc=..&apv=.." shape already built by MainActivity.java
// (Android, live-tap JS bridge) and firebase-messaging-sw.js (web, postMessage) into the
// same target shape used by mapClickToTarget.
export function parseRedirectTarget(redirectUrl: string): NotificationTarget | null {
    const queryIndex = redirectUrl.indexOf("?");
    const query = queryIndex >= 0 ? redirectUrl.slice(queryIndex + 1) : "";
    const params = new URLSearchParams(query);
    const gtc = params.get("gtc");
    const wpc = params.get("wpc");
    const tkc = params.get("tkc");
    const apv = params.get("apv");
    if (!gtc && !wpc && !tkc && !apv) return null;
    return { gtc, wpc, tkc, apv };
}

// Resolves a chat/workplace/task id that may not be loaded in the store yet (validates +
// fetches via the backend, same as a normal in-app click would), loads it into Redux, then
// navigates. Shared by DirectRedirection.tsx (cold start / URL fallback, may run before
// backend session is even set up) and the live notification-tap listeners registered in
// routes/index.tsx (app already running - no page reload, state is preserved).
export async function resolveAndNavigateNotification(
    target: NotificationTarget,
    { dispatch, navigate }: { dispatch: any; navigate: (url: string) => void }
): Promise<void> {
    const { gtc, wpc, tkc, apv } = target;
    const api = new APIClient();

    if (apv) {
        dispatch(changeTab(TABS.APPROVAL));
        navigate("/dashboard");
        return;
    }

    const fnGoToWorkPlace = async (wpMode: WpModes, activePostId: string, memberRole: MemberRole) => {
        const state = getStore().getState();
        const activeMemberRole = state.WpSlice.activeMemberRole;
        const activeTab = state.Layout.activeTab;
        const tab = wpMode === WpModes.Workplace ? TABS.WORKPLACE : TABS.TASK;
        // Set before dispatching the role change below (or anything else that can kick off a
        // title-list reload as a side effect) so wpSaga.ts's loadPostTitleSaga sees the flag and
        // force-includes this post, instead of racing it and reading the flag as unset.
        Utils.SetRedirectPostId(wpMode, activePostId);
        if (tab !== activeTab || (activeMemberRole !== memberRole && activeMemberRole !== MemberRole.All)) {
            await dispatch(setActiveMemberrole(wpMode, memberRole));
        }
        setTimeout(() => {
            dispatch(setActivePostId2(wpMode, activePostId, null, null, null, null, "Notification GoTo " + (wpMode === WpModes.Workplace ? "Workplace" : "Task")));
        }, 700);
    };

    if (gtc) {
        try {
            const response = await api.fnPostForm("/check-redirect-openchat", { userOrGroupId: gtc });
            const chatId = Number(gtc);
            dispatch(changeTab(TABS.CHAT));
            dispatch(getChatUserDetails2s(chatId, true, true, false));
            dispatch(setActiveGroupId(chatId));
            dispatch(changeSelectedChat(chatId));
            // Mirrors onSelectChat in Chats/index.tsx - without this, a workplace/task post left
            // open from before the tap keeps activePostId set, and Dashboard/index.tsx's view
            // switch (`activePostId !== null` is checked before `selectedChat`) keeps rendering
            // WorkplaceContent instead of the chat even though selectedChat did update.
            dispatch(setActivePostId2(WpModes.Workplace, null, null, null, null, null, "Notification GoTo Chat - clear active post"));
            // Mirrors onSelectChat in Chats/index.tsx - without this, chatUserConversations
            // never gets fetchType "initial" set, so Conversation.tsx's scroll-to-bottom /
            // scroll-to-first-unread logic never runs and the room opens at the wrong position.
            dispatch(readConversation(chatId));
            dispatch(getChatUserConversations2(chatId, null, null, null, null, null, "Notification tap GoTo Chat"));
            navigate(response.url);
        } catch (err) {
            navigate("/auth-login");
        }
        return;
    }

    if (wpc || tkc) {
        const postId = (wpc ? wpc : tkc) ?? "";
        try {
            const response = await api.fnPostForm("/check-redirect-openwp", { postIdParent: postId });
            if (response.msg) {
                await Utils.SweetAlert(response.msg);
                navigate("/dashboard");
            } else if (wpc) {
                dispatch(changeTab(TABS.WORKPLACE));
                await fnGoToWorkPlace(WpModes.Workplace, postId, response.memberRole);
                navigate(response.url);
            } else if (tkc) {
                dispatch(changeTab(TABS.TASK));
                await fnGoToWorkPlace(WpModes.Task, postId, response.memberRole);
                navigate(response.url);
            }
        } catch (err) {
            await Utils.SweetAlert(Utils.GetMessageFromExecption(err));
            navigate("/dashboard");
        }
    }
}
